import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  historyResponseSchema,
  linkSchema,
  mapLayerSchema,
  monitorEventSchema,
  monitorSchema,
  nodeAddressSchema,
  nodeGroupSchema,
  nodeSchema,
  siteSchema,
  topologySchema,
  type CreateLink,
  type CreateMapLayer,
  type CreateMonitor,
  type CreateNode,
  type CreateNodeGroup,
  type Monitor,
  type MonitorEvent,
  type NetworkNode,
  type NodeGroup,
  type ReplaceNodeAddresses,
  type Site,
  type Topology,
  type UpdateLink,
  type UpdateMapLayer,
  type UpdateMonitor,
  type UpdateNode,
  type UpdateNodeGroup,
  type UpdateSite,
  type UpdatePositions,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';

export const topologyKey = (siteId: string) => ['topology', siteId] as const;
export const monitorsKey = (nodeId: string) => ['monitors', nodeId] as const;
export const eventsKey = (siteId: string) => ['events', siteId] as const;
export const historyKey = (monitorId: string) => ['history', monitorId] as const;

/** Правка топологии прямо в кэше — без перезапроса всей карты. */
function patchTopology(
  queryClient: QueryClient,
  siteId: string | null,
  change: (topology: Topology) => Topology,
): void {
  queryClient.setQueryData(topologyKey(siteId ?? ''), (previous?: Topology) =>
    previous ? change(previous) : previous,
  );
}

/**
 * Показать правку, не дожидаясь ответа.
 *
 * Одиночные правки карты — стиль связи, плотность заливки рамки, имя узла —
 * пользователь ждёт применёнными в том же кадре, в котором нажал. Круг
 * к серверу это в лучшем случае десятки миллисекунд, а с проверкой прав
 * и записью в журнал — заметная задержка, и всё это время карта показывает
 * прежнее значение, будто нажатие не сработало.
 *
 * Поэтому правка сначала ложится в кэш, а запрос идёт следом: ответ сервера
 * перезапишет то же самое значение, а ошибка отправит карту перезапрашиваться
 * (`onError` у каждой мутации) — показанное оптимистично иначе осталось бы
 * на карте как настоящее.
 *
 * Идущий запрос топологии перед этим отменяется: его ответ старше правки
 * и затёр бы её.
 */
async function showAtOnce(
  queryClient: QueryClient,
  siteId: string | null,
  change: (topology: Topology) => Topology,
): Promise<void> {
  await queryClient.cancelQueries({ queryKey: topologyKey(siteId ?? '') });
  patchTopology(queryClient, siteId, change);
}

/**
 * Рамка и всё, что в неё вложено.
 *
 * Сервер удаляет вложенные рамки вместе с родительской (`on delete cascade`),
 * и кэш обязан повторить это в точности: иначе на карте останется рамка,
 * которой на сервере уже нет, и пропадёт она только при следующем полном
 * запросе схемы.
 */
function withNested(groups: readonly NodeGroup[], groupId: string): Set<string> {
  const doomed = new Set([groupId]);

  let grew = true;
  while (grew) {
    grew = false;
    for (const group of groups) {
      if (group.parentId !== null && doomed.has(group.parentId) && !doomed.has(group.id)) {
        doomed.add(group.id);
        grew = true;
      }
    }
  }

  return doomed;
}

/**
 * Поля правки без незаданных.
 *
 * `{ ...item, ...input }` иначе затрёт значение явным `undefined`: правка
 * приходит частичной, и ключ в ней может присутствовать пустым.
 */
function defined<T extends object>(input: T): T {
  return Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T;
}

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => apiRequest('/sites', z.array(siteSchema)),
  });
}

/**
 * Карта площадки. Обновление по таймеру не нужно: статусы приходят
 * по WebSocket, а состав узлов меняется только нашими же действиями.
 */
/**
 * Правка площадки. Ответ кладётся в кэш, а не перезапрашивается: смена шага
 * сетки должна отражаться на карте сразу, а лишний круг к серверу — это
 * пересборка всей карты.
 */
export function useUpdateSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, input }: { siteId: string; input: UpdateSite }) =>
      apiRequest(`/sites/${siteId}`, siteSchema, { method: 'PATCH', body: input }),
    onSuccess: (site) => {
      queryClient.setQueryData(['sites'], (previous?: Site[]) =>
        previous?.map((item) => (item.id === site.id ? site : item)),
      );
    },
  });
}

export function useTopology(siteId: string | null) {
  return useQuery({
    queryKey: topologyKey(siteId ?? ''),
    queryFn: () => apiRequest(`/sites/${siteId ?? ''}/topology`, topologySchema),
    enabled: siteId !== null,
  });
}

/**
 * Мониторы узла. Обновляются по таймеру, а не только по WebSocket.
 *
 * По WebSocket приходят **изменения статуса** (§8.3), а успешная проверка
 * статус не меняет — значит, ни одного сообщения. Без опроса строка
 * «проверен N назад» росла бы до бесконечности при исправно работающих
 * проверках, то есть показывала бы ровно обратное действительности.
 *
 * Слать каждую проверку в WebSocket нельзя: на тысяче мониторов это поток
 * в тридцать сообщений в секунду каждому открытому браузеру, и §8.3 прямо
 * задаёт дельты. Опрос здесь дёшев, потому что запрашивается один узел:
 * это несколько строк раз в двадцать секунд, пока панель открыта.
 */
export function useMonitors(nodeId: string | null) {
  return useQuery({
    queryKey: monitorsKey(nodeId ?? ''),
    queryFn: () => apiRequest(`/nodes/${nodeId ?? ''}/monitors`, z.array(monitorSchema)),
    enabled: nodeId !== null,
    refetchInterval: 20_000,
  });
}

/**
 * История проверок за последний час — мини-график RTT в панели узла
 * (§11, фаза 4, задача 5).
 *
 * Разрешение не задаётся: сервер выбирает его по длине периода, и на часе
 * это сырые точки. Граница периода округляется до минуты, иначе каждый
 * повторный рендер давал бы новый ключ запроса и новый запрос.
 */
export function useMonitorHistory(monitorId: string | null, enabled = true) {
  const until = Math.floor(Date.now() / 60_000) * 60_000;

  return useQuery({
    queryKey: [...historyKey(monitorId ?? ''), until],
    queryFn: () => {
      const from = new Date(until - 3_600_000).toISOString();
      const to = new Date(until).toISOString();

      return apiRequest(
        `/monitors/${monitorId ?? ''}/history?from=${from}&to=${to}`,
        historyResponseSchema,
      );
    },
    enabled: enabled && monitorId !== null,
    staleTime: 60_000,
  });
}

export function useEvents(siteId: string | null, limit = 50) {
  return useQuery({
    queryKey: eventsKey(siteId ?? ''),
    queryFn: () =>
      apiRequest(`/sites/${siteId ?? ''}/events?limit=${limit}`, z.array(monitorEventSchema)),
    enabled: siteId !== null,
    refetchInterval: 15_000,
  });
}

/**
 * Слои карты площадки.
 *
 * Отдельных запросов на чтение нет: слои приходят вместе с топологией —
 * без них карту не нарисовать. Здесь только правка, и она сбрасывает
 * топологию: узлы и рамки ссылаются на слои, и показывать схему по старому
 * составу нельзя.
 */
export function useCreateLayer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ siteId, input }: { siteId: string; input: CreateMapLayer }) =>
      apiRequest(`/sites/${siteId}/layers`, mapLayerSchema, { method: 'POST', body: input }),
    onSuccess: (_layer, { siteId }) =>
      queryClient.invalidateQueries({ queryKey: topologyKey(siteId) }),
  });
}

export function useUpdateLayer(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ layerId, input }: { layerId: string; input: UpdateMapLayer }) =>
      apiRequest(`/layers/${layerId}`, mapLayerSchema, { method: 'PATCH', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

export function useRemoveLayer(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (layerId: string) =>
      apiRequest(`/layers/${layerId}`, z.unknown(), { method: 'DELETE', acceptStatuses: [204] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/**
 * Создание узла. Ответ кладётся в кэш, а не перезапрашивается.
 *
 * Перезапрос — это вся схема площадки целиком; на карте из пятисот узлов
 * он занимает заметное время, и всё это время созданного узла на экране нет,
 * будто кнопка не сработала. Сервер уже вернул готовый узел — большего
 * карте и не нужно.
 */
export function useCreateNode(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateNode) =>
      apiRequest(`/sites/${siteId ?? ''}/nodes`, nodeSchema, { method: 'POST', body: input }),
    onSuccess: (node) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        nodes: [...topology.nodes, node],
      })),
  });
}

/**
 * Изменение узла. Правка показывается сразу, а ответ сервера кладётся
 * в кэш вместо перезапроса топологии: сервер уже вернул обновлённый узел,
 * и повторный запрос всей карты — это лишний круг ради того же самого.
 */
export function useUpdateNode(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, input }: { nodeId: string; input: UpdateNode }) =>
      apiRequest(`/nodes/${nodeId}`, nodeSchema, { method: 'PATCH', body: input }),
    onMutate: ({ nodeId, input }) =>
      showAtOnce(queryClient, siteId, (topology) => ({
        ...topology,
        nodes: topology.nodes.map((item) =>
          item.id === nodeId ? { ...item, ...defined(input) } : item,
        ),
      })),
    onSuccess: (node) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        nodes: topology.nodes.map((item) => (item.id === node.id ? node : item)),
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/**
 * Замена списка дополнительных адресов узла (ADR 035).
 *
 * Оптимистичной правки здесь нет намеренно, в отличие от прочих правок узла:
 * сервер разворачивает диапазон в адреса и заводит каждому служебный монитор,
 * и повторять этот расчёт в кэше значило бы держать вторую его реализацию —
 * с идентификаторами, которых у браузера всё равно нет. Ответ приходит полным
 * списком и ложится в схему как есть.
 */
export function useReplaceNodeAddresses(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ nodeId, input }: { nodeId: string; input: ReplaceNodeAddresses }) =>
      apiRequest(`/nodes/${nodeId}/addresses`, z.array(nodeAddressSchema), {
        method: 'PUT',
        body: input,
      }),
    onSuccess: (addresses, { nodeId }) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        addresses: [
          ...topology.addresses.filter((address) => address.nodeId !== nodeId),
          ...addresses,
        ],
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/**
 * Удаление узла. Узел уходит с карты сразу, а запрос идёт следом: ждать
 * ответа, глядя на узел, который уже подтвердили удалить, — это выглядит
 * как незамеченное нажатие, и Delete жмут второй раз.
 */
export function useDeleteNode(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (nodeId: string) =>
      apiRequest(`/nodes/${nodeId}`, z.unknown(), { method: 'DELETE', acceptStatuses: [204] }),
    onMutate: (nodeId) =>
      showAtOnce(queryClient, siteId, (topology) => ({
        ...topology,
        nodes: topology.nodes.filter((node) => node.id !== nodeId),
        // Связь, у которой не стало конца, сервер в схеме больше не отдаст:
        // на карте ей неоткуда взяться и здесь.
        links: topology.links.filter(
          (link) => link.sourceId !== nodeId && link.targetId !== nodeId,
        ),
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

export function useCreateMonitor(nodeId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMonitor) =>
      apiRequest(`/nodes/${nodeId ?? ''}/monitors`, monitorSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: monitorsKey(nodeId ?? '') }),
  });
}

export function useDeleteMonitor(nodeId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (monitorId: string) =>
      apiRequest(`/monitors/${monitorId}`, z.unknown(), {
        method: 'DELETE',
        acceptStatuses: [204],
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: monitorsKey(nodeId ?? '') }),
  });
}

/**
 * Изменение монитора. Тип проверки не меняется — у каждого типа своя
 * конфигурация, и смена типа на месте оставила бы монитор с чужими
 * параметрами (§7.5).
 */
/**
 * Изменение монитора. Правка видна сразу, а ответ кладётся в кэш вместо
 * перезапроса списка.
 *
 * Так же, как с картой в фазе 4, и по той же причине: выключатель монитора
 * иначе ждал круга к серверу да ещё и перезапроса всего списка — два круга
 * вместо нуля, и всё это время кнопка показывает прежнее состояние.
 */
export function useUpdateMonitor(nodeId: string | null) {
  const queryClient = useQueryClient();
  const key = monitorsKey(nodeId ?? '');

  return useMutation({
    mutationFn: ({ monitorId, input }: { monitorId: string; input: UpdateMonitor }) =>
      apiRequest(`/monitors/${monitorId}`, monitorSchema, { method: 'PATCH', body: input }),
    onMutate: async ({ monitorId, input }) => {
      await queryClient.cancelQueries({ queryKey: key });

      const previous = queryClient.getQueryData<Monitor[]>(key);

      queryClient.setQueryData(key, (monitors?: Monitor[]) =>
        monitors?.map((item) => (item.id === monitorId ? { ...item, ...defined(input) } : item)),
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(key, context.previous);
      }
    },
    onSuccess: (monitor) =>
      queryClient.setQueryData(key, (monitors?: Monitor[]) =>
        monitors?.map((item) => (item.id === monitor.id ? monitor : item)),
      ),
  });
}

/**
 * Внеочередная проверка. Ответ пустой и приходит сразу: результат придёт
 * телеметрией через WebSocket, а не в ответе на этот запрос.
 */
export function useCheckNow() {
  return useMutation({
    mutationFn: (monitorId: string) =>
      apiRequest(`/monitors/${monitorId}/check-now`, z.unknown(), {
        method: 'POST',
        acceptStatuses: [202],
      }),
  });
}

// — Карта: связи, группы, координаты ————————————————————

/**
 * Массовое обновление координат (§8.2).
 *
 * После успеха новые координаты кладутся прямо в кэш топологии, а не
 * запрашиваются заново: перезагрузка дёрнула бы карту с миганием ради тех же
 * значений. Но и не обновлять кэш нельзя — тогда любая пересборка карты
 * (переключение режима, фильтр) возвращала бы узлы на прежние места.
 *
 * При ошибке кэш сбрасывается: там расхождение реально, и показывать
 * несохранённое положение как сохранённое нельзя.
 */
export function useSavePositions(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdatePositions) =>
      apiRequest(`/sites/${siteId ?? ''}/nodes/positions`, z.object({ updated: z.number() }), {
        method: 'POST',
        body: input,
      }),
    onSuccess: (_result, input) => {
      queryClient.setQueryData(topologyKey(siteId ?? ''), (previous?: Topology) => {
        if (!previous) {
          return previous;
        }

        const moved = new Map(input.map((position) => [position.id, position]));

        return {
          ...previous,
          nodes: previous.nodes.map((node) => {
            const position = moved.get(node.id);
            if (!position) {
              return node;
            }
            return {
              ...node,
              x: position.x,
              y: position.y,
              ...(position.groupId === undefined ? {} : { groupId: position.groupId }),
            };
          }),
        };
      });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/** Создание связи. Ответ в кэш — по той же причине, что и у узла. */
export function useCreateLink(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateLink) =>
      apiRequest(`/sites/${siteId ?? ''}/links`, linkSchema, { method: 'POST', body: input }),
    onSuccess: (link) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        links: [...topology.links, link],
      })),
  });
}

/**
 * Изменение связи. Показывается сразу, а ответ кладётся в кэш вместо
 * перезапроса топологии: сервер уже вернул обновлённую связь, а лишний круг
 * к серверу — это пересборка карты и заметная задержка на каждое нажатие
 * в панели.
 */
export function useUpdateLink(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ linkId, input }: { linkId: string; input: UpdateLink }) =>
      apiRequest(`/links/${linkId}`, linkSchema, { method: 'PATCH', body: input }),
    onMutate: ({ linkId, input }) =>
      showAtOnce(queryClient, siteId, (topology) => ({
        ...topology,
        links: topology.links.map((item) =>
          item.id === linkId ? { ...item, ...defined(input) } : item,
        ),
      })),
    onSuccess: (link) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        links: topology.links.map((item) => (item.id === link.id ? link : item)),
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

export function useDeleteLink(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (linkId: string) =>
      apiRequest(`/links/${linkId}`, z.unknown(), { method: 'DELETE', acceptStatuses: [204] }),
    onMutate: (linkId) =>
      showAtOnce(queryClient, siteId, (topology) => ({
        ...topology,
        links: topology.links.filter((link) => link.id !== linkId),
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/** Создание рамки. Ответ в кэш — по той же причине, что и у узла. */
export function useCreateGroup(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateNodeGroup) =>
      apiRequest(`/sites/${siteId ?? ''}/groups`, nodeGroupSchema, { method: 'POST', body: input }),
    onSuccess: (group) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        groups: [...topology.groups, group],
      })),
  });
}

/**
 * Изменение группы. Как и с координатами узлов, ответ кладётся в кэш:
 * перетащенная рамка не должна прыгать обратно при следующей пересборке
 * карты. Цвет, имя и плотность заливки показываются, не дожидаясь ответа.
 */
export function useUpdateGroup(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, input }: { groupId: string; input: UpdateNodeGroup }) =>
      apiRequest(`/groups/${groupId}`, nodeGroupSchema, { method: 'PATCH', body: input }),
    onMutate: ({ groupId, input }) =>
      showAtOnce(queryClient, siteId, (topology) => ({
        ...topology,
        groups: topology.groups.map((item) =>
          item.id === groupId ? { ...item, ...defined(input) } : item,
        ),
      })),
    onSuccess: (group) =>
      patchTopology(queryClient, siteId, (topology) => ({
        ...topology,
        groups: topology.groups.map((item) => (item.id === group.id ? group : item)),
      })),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

/**
 * Удаление рамки. Уходит с карты сразу, вместе с вложенными рамками:
 * сервер удаляет их каскадом, а узлы оставляет, вынув наружу.
 */
export function useDeleteGroup(siteId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) =>
      apiRequest(`/groups/${groupId}`, z.unknown(), { method: 'DELETE', acceptStatuses: [204] }),
    onMutate: (groupId) =>
      showAtOnce(queryClient, siteId, (topology) => {
        const doomed = withNested(topology.groups, groupId);

        return {
          ...topology,
          groups: topology.groups.filter((group) => !doomed.has(group.id)),
          nodes: topology.nodes.map((node) =>
            node.groupId !== null && doomed.has(node.groupId) ? { ...node, groupId: null } : node,
          ),
        };
      }),
    onError: () => queryClient.invalidateQueries({ queryKey: topologyKey(siteId ?? '') }),
  });
}

export type { Monitor, MonitorEvent, NetworkNode, Site, Topology };
